import { useEffect, useState } from 'react';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';
import {
  DocumentNode,
  OperationVariables,
  TypedDocumentNode,
  useQuery,
} from '@apollo/client';

import LoadingSpinner from '../LoadingSpinner';
import ErrorText from '../ErrorText';
import DraggableAttribute from './DraggableAttribute';
import { TYPES } from '../../reducers/builderReducer';
import type { MembershipResourceType } from '../../types/dataDictionary';

const { MY_DATA_ATTRIBUTE } = TYPES;

/** ATAD-146: how often `pollWhile` re-runs the query while it holds. */
export const DRAGGABLE_QUERY_LIST_POLL_MS = 3000;

interface DraggableQueryListProps {
  className?: string;
  label: string;
  query: DocumentNode | TypedDocumentNode<any, OperationVariables>;
  initialOpen?: boolean;
  selector: string;
  resourceType: MembershipResourceType;
  /** Narrow/sort the query's items (e.g. only ZIP code files, newest first). */
  filterItems?: (items: any[]) => any[];
  /**
   * ATAD-146: re-run the query every few seconds while this returns true for
   * the (filtered) items — e.g. while a model file is importing. Items are
   * draggable only once `crawledAt` is set, so without a poll a finished
   * import stays greyed out until the page is reloaded.
   */
  pollWhile?: (items: any[]) => boolean;
  /**
   * ATAD-146: render nothing at all while the (filtered) list is empty —
   * a section the user has no way to fill is noise.
   */
  hideWhenEmpty?: boolean;
  /** ATAD-146: a line under an item's name, e.g. why it can't be dragged yet. */
  renderItemStatus?: (item: any) => React.ReactNode;
}

export default function DraggableQueryList({
  className,
  label,
  query,
  initialOpen = false,
  selector,
  resourceType,
  filterItems,
  pollWhile,
  hideWhenEmpty = false,
  renderItemStatus,
}: DraggableQueryListProps) {
  const [open, setOpen] = useState(initialOpen);

  const { loading, error, data, startPolling, stopPolling } = useQuery(query, {
    fetchPolicy: 'cache-and-network',
  });

  const allItems = data?.[selector] || [];
  const listItems = filterItems ? filterItems(allItems) : allItems;

  const shouldPoll = pollWhile ? pollWhile(listItems) : false;
  useEffect(
    function pollWhileAsked() {
      if (!shouldPoll) return undefined;
      startPolling(DRAGGABLE_QUERY_LIST_POLL_MS);
      return () => stopPolling();
    },
    [shouldPoll, startPolling, stopPolling]
  );

  // Hidden while loading too, so the section doesn't flash up and vanish.
  if (hideWhenEmpty && !error && listItems.length === 0) return null;

  return (
    <div className={`w-full ${className ? className : ''}`}>
      <div
        onClick={() => setOpen((prevState) => !prevState)}
        className="flex flex-row items-center hover:bg-gray-200 cursor-pointer p-1 -ml-2 rounded-lg"
      >
        <div>{open ? <FaAngleUp /> : <FaAngleDown />}</div>
        <div className="pl-1">{label}</div>
      </div>
      <div
        className={`py-2 pl-5 flex flex-row h-full w-full overflow-auto ${
          open ? 'visible' : 'hidden'
        }`}
      >
        {loading && (
          <LoadingSpinner message={`Getting your ${label?.toLowerCase()}...`} />
        )}
        {!loading && error && <ErrorText message={error.message} />}
        {!loading && !error && listItems && (
          <ul>
            {listItems.map((item, idx) => {
              const isDisabled = item.crawledAt == null;
              const status = renderItemStatus?.(item);

              return (
                <li className="py-0.5" key={item.id || idx}>
                  <DraggableAttribute
                    id={item.id}
                    name={item.name}
                    resourceType={resourceType}
                    type={MY_DATA_ATTRIBUTE}
                    disabled={isDisabled}
                  />
                  {status && <div className="pl-1 mt-0.5">{status}</div>}
                </li>
              );
            })}
            {listItems?.length === 0 && <p>{`No ${label?.toLowerCase()}`}</p>}
          </ul>
        )}
      </div>
    </div>
  );
}
